import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";

import { fundedComplianceActions } from "../../../../reducers/FundedCompliance";
import { FUNDED_COMPLIANCE_API_URLS, FundedComplianceAPI } from "../../../../api/fundedCompliance";
import { SUBMISSION_TO_PROP_COLUMNS } from "./constants";
import { Table, FilterAutocomplete, Select, StatusColorLegend } from "../../../../components/ui";
import usePagination from "../../../../hooks/usePagination";
import newSortBy from "../../../../utils/newSortBy";
import { ISortBy } from "../../../../types";
import { SUBMISSION_TO_PROP_FILTER_OPTIONS } from "./constants";
import { appendSearchParamsToQuery } from "../../../ComplianceSearch/utils";
import { COMPLIANCE_STATUS_OPTIONS } from "../../../../components/ui/AccountComplianceStatusSelect";
import { STATUS_COLORS_OPACITY } from "../../../../constants";

const SubmissionToProp = (): React.ReactElement => {
  const dispatch = useDispatch();
  const pagination = usePagination();

  const [complianceStatus, setComplianceStatus] = useState<string>("all");

  const [search, setSearch] = useState<{
    "Group id": string;
    Name: string;
    Email: string;
  }>({
    "Group id": "",
    Name: "",
    Email: "",
  });

  const [sortBy, setSortBy] = useState<ISortBy>({ by: "timestamp", order: false });
  const { submissionToProp } = useSelector((state) => state.fundedCompliance);

  const handleSortBy = (by: string) => newSortBy({ by, sortBy, setSortBy });

  const { isError, error } = useQuery({
    queryKey: [
      FUNDED_COMPLIANCE_API_URLS.submissionToPropList,
      pagination.page,
      pagination.limit,
      complianceStatus,
      search,
      sortBy,
    ],
    queryFn: async ({ signal }) => {
      const query = new URLSearchParams();
      query.append("page", String(pagination.page));
      query.append("limit", String(pagination.limit));
      query.append("sortBy", sortBy.by);
      query.append("order", sortBy.order ? "asc" : "desc");

      appendSearchParamsToQuery({ ...search, compliance_status: complianceStatus }, query);

      dispatch(fundedComplianceActions.fetchSubmissionToPropRequest());
      const response = await FundedComplianceAPI.getSubmissionToPropListRequest(
        query.toString(),
        signal
      );
      dispatch(
        fundedComplianceActions.fetchSubmissionToPropSuccess(response.data.submissionToPropList)
      );
      pagination.setCount(response.data.submissionToPropList.count);
      return response.data;
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(fundedComplianceActions.fetchSubmissionToPropFailed());
    }
  }, [isError, error]);

  useEffect(() => {
    pagination.setPage(0);
  }, [...Object.values(search)]);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={10}>
          <FilterAutocomplete
            search={search}
            setSearch={setSearch}
            filters={SUBMISSION_TO_PROP_FILTER_OPTIONS}
          />
        </Grid>
        <Grid item xs={2}>
          <Select
            label="Compliance Status"
            inputLabel="Compliance Status"
            name="complianceStatus"
            value={complianceStatus}
            onChange={(e) => setComplianceStatus(e.target.value as string)}
            options={[
              {
                label: "All",
                value: "all",
              },
              ...COMPLIANCE_STATUS_OPTIONS,
            ].map((opt) => ({
              text: opt.label as string,
              name: opt.value,
            }))}
          />
        </Grid>

        <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
          <StatusColorLegend
            legend={{
              multiplePasses: {
                color: STATUS_COLORS_OPACITY.pastelPink,
                name: "Multiple passes during 20 days",
              },
            }}
          />
        </Grid>
      </Grid>

      <Table
        loading={submissionToProp.isLoading}
        columns={SUBMISSION_TO_PROP_COLUMNS}
        data={submissionToProp.data.rows}
        extractKey={(row, index) => `${row.id}${index}`}
        theadTitle="Submission to Prop"
        csv_table_name="Submission to Prop"
        pagination={pagination}
        handleSortBy={handleSortBy}
        sortBy={sortBy}
      />
    </div>
  );
};

export default SubmissionToProp;
